import { createElement, Suspense } from "react";
import style from "style-to-object";
import { Link } from "react-router-dom";
import {
  convertHtmlToReact,
  convertNodeToReactElement
} from "@hedgedoc/html-to-react";
import { unwrapAsset } from "../../utils/CockpitUtils";
import { cmsUrl, findFirstHeading, transformHtml } from "../../utils/CmsUtils";
import Herobox from "../Herobox";
import HeroCarousel from "../HeroCarousel";
import ContentSection from "../ContentSection";
import CockpitProductGrid from "./CockpitProductGrid";
import EnterViewportAnimation from "../EnterViewportAnimation";
import { CockpitStoryGrid } from "../StoryGrid";
import StoryImage from "../StoryImage";
import { CockpitNewsGrid } from "../NewsGrid";
import ImageOrVideo from "../ImageOrVideo";
import ProductLink from "../ProductLink";
import StorySlide from "../HeroCarousel/StorySlide";
import NewsSlide from "../HeroCarousel/NewsSlide";
import ProductsMenu from "../dropdown/ProductsMenu";
import LinkList from "../dropdown/LinkList";
import NewsGridMenu from "../dropdown/NewsGridMenu";
import StoryGridMenu from "../dropdown/StoryGridMenu";
import FaqListMenu from "../dropdown/FaqListMenu";
import GuideListMenu from "../dropdown/GuideListMenu";
import GuideStepCard from "../Guide/GuideStepCard";
import { CockpitClaimTypeList } from "../ClaimTypeList";
import { Spinner } from "reactstrap";
import JotForm from "../JotForm";
import BreadcrumbPageItem from "../BreadcrumbPageItem";
import Icon from "../Icon";
import Breadcrumbs from "../Breadcrumbs";
import CockpitTable from "./CockpitTable";
import CockpitYoutubePlayer from "./CockpitYoutubePlayer";
import USPList from "../dropdown/USPList";
import RoomEditorButton from "../RoomEditor/button";
import CockpitAnimation from "./CockpitAnimation";
import { CockpitCategoryGrid } from "../CategoryGrid";
import CategoryGridMenu from "../dropdown/CategoryGridMenu";
import RenderWhenInView from "../../functional-components/RenderWhenInView";
import Guide from "../Guide/Guide";
import SectionNavigation from "../SectionNavigation";
import ImageBox from "../ImageBox";
import PageFragment from "../PageFragment";
import PopupFragmentButton from "../PageFragment/PopupFragmentButton";
import ImageCollage from "../ImageCollage";
import AssetList from "../AssetList";
import GridLayout from "../GridLayout";
import GridCell from "../GridLayout/GridCell";
import ImageCell from "../GridLayout/ImageCell";
import CategoryCell from "../GridLayout/CategoryCell";
import StoryCell from "../GridLayout/StoryCell";
import ProductCell from "../GridLayout/ProductCell";
import NewsCell from "../GridLayout/NewsCell";
import SubMenu from "../SubMenu";
import HeroDetailsBox from "../Herobox/DetailsBox";
import classNames from "classnames";
import StoryPreviewLarge from "../StoryPreview/large";
import PeopleGrid from "../People/grid";
import HiddenHeading from "../HiddenHeading";

function htmlProps(data) {
  const props = {
    id: data.settings.id || null,
    className: data.settings.class || null
  };
  if (data.settings.style) props.style = style(data.settings.style);
  return props;
}

function settingsProps(data) {
  const {
    id: _id,
    class: _class,
    style: _style,
    key: _key,
    ...rest
  } = data.settings;
  return { ...rest };
}

function addClass(data, className, before) {
  if (data.settings) {
    const classes =
      typeof data.settings.class === "string"
        ? data.settings.class.split(" ")
        : [];
    if (!classes.includes(className)) {
      if (before) {
        classes.unshift(className);
      } else {
        classes.push(className);
      }
      data.settings.class = classes.filter(c => c !== "").join(" ");
    }
  }
  return data;
}

function addClassConditional(data, className, condition) {
  return condition ? addClass(data, className) : data;
}

/*function addStyle(data, style) {
  if (data.settings) {
    data.settings.style = ((data.settings.style || "") + " " + style).trim();
  }
  return data;
}*/

function transformEmbeddedMap(node, index) {
  if (node.type === "tag" && node.name === "iframe") {
    delete node.attribs.height;
    delete node.attribs.width;
    delete node.attribs.style;
  }
  return convertNodeToReactElement(node, index, transformEmbeddedMap);
}

function formatPhone(phone) {
  return "tel:" + phone.replace(new RegExp("\\(.*\\)"), "");
}

export function cockpitComponent(data, key) {
  addClass(data, "cockpit-" + data.component);
  const defaultAriaLabel = findFirstHeading(data);

  switch (data.component) {
    // Default cockpit components
    case "section":
      return (
        <section {...htmlProps(data)} key={key} aria-label={defaultAriaLabel}>
          {data.children.map(cockpitComponent)}
        </section>
      );
    case "heading":
      return (
        <div {...htmlProps(data)} key={key}>
          {createElement(data.settings.tag || "h1", null, data.settings.text)}
        </div>
      );
    case "text":
      return (
        <div {...htmlProps(data)} key={key}>
          {transformHtml(data.settings.text)}
        </div>
      );
    case "html":
      return (
        <div {...htmlProps(data)} key={key}>
          {transformHtml(data.settings.html)}
        </div>
      );
    case "grid":
      data.columns.forEach((column, i) => {
        column.component = "column";
        column.col = i;
        column.cols = data.columns.length;
      });
      return (
        <div {...htmlProps(data)} key={key}>
          {data.columns.map(cockpitComponent)}
        </div>
      );
    case "column":
      return (
        <div {...htmlProps(data)} key={key}>
          {data.children.map(cockpitComponent)}
        </div>
      );
    case "image":
      return (
        <img
          {...htmlProps(data)}
          key={key}
          src={cmsUrl(data.settings.image.path)}
          alt={""}
        />
      );
    case "button":
      return (
        <button {...htmlProps(data)} key={key} href={data.settings.url}>
          {transformHtml(data.settings.text)}
        </button>
      );
    case "divider":
      return <hr {...htmlProps(data)} key={key} />;
    // Custom components
    // Child components
    case "bjelin_heading":
      addClassConditional(
        data,
        "text-" + data.settings.alignment,
        data.settings.alignment
      );
      return (
        <div {...htmlProps(data)} key={key}>
          {createElement(data.settings.tag || "h1", null, data.settings.text)}
        </div>
      );
    case "sr_heading":
      return (
        <HiddenHeading
          {...htmlProps(data)}
          key={key}
          text={data.settings.text || "?"}
        />
      );
    case "bjelin_button": {
      addClassConditional(
        data,
        "text-" + data.settings.alignment,
        data.settings.alignment
      );
      const icon = {};
      if (data.settings.icon_position && data.settings.icon) {
        icon[data.settings.icon_position] = <Icon name={data.settings.icon} />;
        if (data.settings.icon_position === "before") {
          data.settings.text = " " + data.settings.text;
        } else {
          data.settings.text += " ";
        }
      }
      if (!data.settings.url) {
        console.warn("Button without URL", data);
        data.settings.url = "#";
      }
      const isExternalUrl = /^https?:\/\//i;
      return (
        <div {...htmlProps(data)} key={key}>
          {isExternalUrl.test(data.settings.url) ? (
            <a
              className={"button-" + data.settings.variant}
              href={data.settings.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {icon.before}
              {data.settings.text}
              {icon.after}
            </a>
          ) : (
            <Link
              className={"button-" + data.settings.variant}
              to={data.settings.url}
            >
              {icon.before}
              {data.settings.text}
              {icon.after}
            </Link>
          )}
        </div>
      );
    }
    case "button_group":
      addClassConditional(
        data,
        "align-" + data.settings.alignment,
        data.settings.alignment
      );
      return (
        <div {...htmlProps(data)} key={key}>
          {data.children.map(cockpitComponent)}
        </div>
      );
    case "bjelin_image": {
      addClassConditional(
        data,
        "text-" + data.settings.alignment,
        data.settings.alignment
      );
      addClass(data, "image-size-" + data.settings.size || "normal");
      const image = unwrapAsset(data.settings.asset);
      const caption = !data.settings.caption ? null : (
        <span className={"caption"}>{data.settings.caption}</span>
      );
      const byline = !data.settings.byline ? null : (
        <span className={"byline"}>{data.settings.byline}</span>
      );
      const product_link = data.settings?.product_link?._id ? (
        <span className="product_link">
          <ProductLink
            showPrefix
            id={data.settings.product_link._id}
            style={{
              fontWeight: "500"
            }}
          />
        </span>
      ) : null;
      const imageRatio =
        image && image.width ? image.height / image.width : 0.3;
      return (
        <div {...htmlProps(data)} key={key}>
          <EnterViewportAnimation key={key}>
            <div
              style={{
                position: "relative",
                maxWidth: "100%",
                paddingTop: imageRatio * 100 + "%"
              }}
            >
              <ImageOrVideo
                image={image}
                caption={data.settings.caption}
                altText={data.settings.image_alt_text}
              />
            </div>
          </EnterViewportAnimation>
          {(byline || caption) && (
            <div className={"caption-byline-row"}>
              {caption}
              {product_link}
              {byline}
            </div>
          )}
        </div>
      );
    }
    case "animation":
      return (
        <CockpitAnimation
          id={data.settings?.animation?._id}
          key={key}
          width={data.settings?.width}
          height={data.settings?.height}
        />
      );
    case "bjelin_text":
      addClassConditional(
        data,
        "text-" + data.settings.alignment,
        data.settings.alignment
      );
      addClassConditional(
        data,
        "responsive-columns",
        data.settings.responsive_multicolumns
      );
      addClassConditional(
        data,
        "columns-" + data.settings.responsive_multicolumns,
        data.settings.responsive_multicolumns
      );
      addClassConditional(data, "well", data.settings.well_theme);
      addClassConditional(
        data,
        "theme-" + data.settings.well_theme,
        data.settings.well_theme
      );
      return (
        <div {...htmlProps(data)} key={key}>
          {transformHtml(data.settings.text)}
        </div>
      );
    case "blockquote":
      addClassConditional(
        data,
        "text-" + data.settings.alignment,
        data.settings.alignment
      );
      addClassConditional(
        data,
        "quote-theme-" + data.settings.theme,
        data.settings.theme
      );
      return (
        <div {...htmlProps(data)} key={key}>
          <EnterViewportAnimation mode="fade-up">
            {data.settings.text}
          </EnterViewportAnimation>
          {data.settings.source && (
            <div className="quote-source">{data.settings.source}</div>
          )}
        </div>
      );
    case "columns": {
      const columns = [];
      for (var i = 0; i < 4; i++) {
        const column = data.settings["column_" + i];
        if (column) {
          columns.push(
            <div className={"column"} key={i}>
              {column.map(cockpitComponent)}
            </div>
          );
        }
      }
      addClass(data, "columns-" + columns.length);
      return (
        <div {...htmlProps(data)} key={key}>
          <div className="cockpit-columns-row">{columns}</div>
        </div>
      );
    }
    case "company_info": {
      const companies = data.settings.companies.map((company, idx) => (
        <div className={"company-entry"} key={idx}>
          <div className={"company-image-column"}>
            {company.value.logo && (
              <img
                src={cmsUrl(company.value.logo.path)}
                alt={company.value.name}
              />
            )}
          </div>
          <div className={"company-info-column"}>
            <h3>{company.value.name}</h3>
            {transformHtml(company.value.information)}
          </div>
        </div>
      ));
      return (
        <div {...htmlProps(data)} key={key}>
          {companies}
        </div>
      );
    } // Parent components
    case "highlight_section":
      addClass(data, "theme-" + (data.settings.theme || "white"));
      return (
        <section
          {...htmlProps(data)}
          key={key}
          aria-label={data.settings.nav_title}
        >
          <div className={"cockpit-highlight_section-content"}>
            <EnterViewportAnimation mode="fade-up">
              {data.children.map(cockpitComponent)}
            </EnterViewportAnimation>
          </div>
        </section>
      );
    case "content_section":
      return (
        <ContentSection
          {...htmlProps(data)}
          fadeIn={!data.settings.disable_fade_in}
          key={key}
          theme={data.settings.theme}
          padded={data.settings.padded}
          sidePadded={data.settings.side_padded}
          noMargins={data.settings.no_margins}
          fullWidth={data.settings.full_width}
          requiresFeature={data.settings.requiresFeature}
          navTitle={data.settings.nav_title}
          ariaLabel={data.settings.nav_title || defaultAriaLabel}
        >
          {data.children.map(cockpitComponent)}
        </ContentSection>
      );
    case "herobox":
      return (
        <Herobox
          {...htmlProps(data)}
          key={key}
          mode={data.settings.mode || "normal"}
          theme={data.settings.theme}
          type={data.settings.size || "fat"}
          backgroundImage={unwrapAsset(data.settings.background_image)}
          centerImage={!!data.settings.center_image}
          textShadow={!!data.settings.text_shadow}
          imageAltText={data.settings.image_alt_text}
          ariaLabel={data.settings.nav_title || defaultAriaLabel}
        >
          {data.children?.length ? (
            <>
              {data.children.map(cockpitComponent)}
              {data.settings?.product_link?._id && (
                <div className="product_link">
                  <ProductLink
                    showPrefix
                    id={data.settings.product_link._id}
                    style={{
                      fontWeight: "500"
                    }}
                  />
                </div>
              )}
            </>
          ) : null}
        </Herobox>
      );
    case "hero_details_box":
      addClass(data, "theme-" + (data.settings.theme || "white"));
      return (
        <HeroDetailsBox {...htmlProps(data, key)} theme={data.settings.theme}>
          {data.children?.length > 0 && data.children.map(cockpitComponent)}
        </HeroDetailsBox>
      );

    case "image_box":
      return (
        <ImageBox
          {...htmlProps(data)}
          key={key}
          theme={data.settings.theme}
          backgroundImage={unwrapAsset(data.settings.background_image)}
          centerImage={!!data.settings.center_image}
          textShadow={!!data.settings.text_shadow}
          imageAltText={data.settings.image_alt_text}
          ariaLabel={data.settings.nav_title || defaultAriaLabel}
        >
          {data.children?.length ? (
            <>
              {data.children.map(cockpitComponent)}
              {data.settings?.product_link?._id && (
                <div className="product_link">
                  <ProductLink
                    showPrefix
                    id={data.settings.product_link._id}
                    style={{
                      fontWeight: "500"
                    }}
                  />
                </div>
              )}
            </>
          ) : null}
        </ImageBox>
      );
    case "image_collage":
      return (
        <ImageCollage
          {...htmlProps(data)}
          key={key}
          images={data.settings.images}
          layout={data.settings.layout}
          crop_images={data.settings.crop_images}
          theme={data.settings.theme}
          caption={data.settings.caption}
          byline={data.settings.byline}
          product_link={data.settings.product_link}
        />
      );
    case "page_fragment":
      return (
        <PageFragment {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "carousel_slide_story":
      return (
        <StorySlide {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "carousel_slide_news":
      return (
        <NewsSlide {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "hero_carousel":
      return (
        <HeroCarousel
          {...htmlProps(data)}
          key={key}
          autoplayInterval={data.settings.autoplay_interval}
          navType={data.settings.navigation_type}
        >
          {data.children}
        </HeroCarousel>
      );
    case "herowell": {
      const themeClassName = "theme-" + (data.settings.theme || "white");
      return (
        <section
          {...htmlProps(data)}
          key={key}
          aria-label={data.settings.nav_title || defaultAriaLabel}
        >
          <ImageOrVideo
            image={unwrapAsset(data.settings.asset)}
            style={{ zIndex: -1 }}
          />
          <div className={"cockpit-herowell-content"}>
            <div className={`cockpit-herowell-well ${themeClassName}`}>
              <EnterViewportAnimation mode="fade-up">
                {data.children.map(cockpitComponent)}
              </EnterViewportAnimation>
            </div>
          </div>
        </section>
      );
    }
    case "side_by_side":
      addClass(data, "theme-" + (data.settings.theme || "white"));
      addClassConditional(
        data,
        "image-right",
        data.settings.image_position === "right"
      );
      return (
        <section
          {...htmlProps(data)}
          key={key}
          aria-label={data.settings.nav_title || defaultAriaLabel}
        >
          <div className="cockpit-side_by_side-row">
            <div
              className="cockpit-side_by_side-image"
              aria-hidden={
                data.settings.image_alt_text === "" ? "true" : "false"
              }
            >
              <RenderWhenInView key={key}>
                <ImageOrVideo
                  image={unwrapAsset(data.settings.asset)}
                  centerImage={!!data.settings.center_image}
                  altText={data.settings.image_alt_text}
                />
              </RenderWhenInView>
            </div>
            <div className="cockpit-side_by_side-content">
              <EnterViewportAnimation mode="fade-up">
                {data.children.map(cockpitComponent)}
              </EnterViewportAnimation>
              {data.settings?.product_link?._id && (
                <div className="cockpit-side_by_side-product_link">
                  <ProductLink
                    showPrefix
                    id={data.settings.product_link._id}
                    style={{
                      fontWeight: "500"
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    case "people_grid":
      return (
        <PeopleGrid
          {...htmlProps(data, key)}
          people={data.settings.people}
          heading={data.settings.heading}
        />
      );
    case "contact_section":
      return (
        <section {...htmlProps(data)} key={key} aria-label="contact">
          <div className="cockpit-contact_section-content">
            {data.children.map(cockpitComponent)}
          </div>
        </section>
      );
    case "location_section":
      return (
        <section {...htmlProps(data)} key={key}>
          {/*{cockpitComponent(data.children[0], 0)}*/}
          {data.children.map(cockpitComponent)}
        </section>
      );
    case "contact_person":
      return (
        <div {...htmlProps(data)} key={key}>
          <div className={"cockpit-contact_person-container"}>
            <div className="cockpit-contact_person-name">
              {data.settings.name}
            </div>
            <div className="cockpit-contact_person-row">
              <div className="cockpit-contact_person-position">
                {data.settings.role} {data.settings.region}
              </div>
              <div className="cockpit-contact_person-contact">
                {data.settings.phone && (
                  <a href={formatPhone(data.settings.phone)}>
                    {data.settings.phone}
                  </a>
                )}
                {data.settings.phone && data.settings.email && " | "}
                {data.settings.email && (
                  <a href={"mailto:" + data.settings.email}>
                    {data.settings.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    case "location":
      return (
        <div {...htmlProps(data)} key={key}>
          <div className={"cockpit-location-container"}>
            <div className={"cockpit-location-info"}>
              <h2>{data.settings.title}</h2>
              {transformHtml(data.settings.text)}
            </div>
            <div className={"cockpit-location-map"}>
              {data.settings.embed_code &&
                convertHtmlToReact(data.settings.embed_code, {
                  transform: transformEmbeddedMap
                })}
            </div>
          </div>
        </div>
      );
    case "story_preview":
      return (
        <StoryPreviewLarge {...htmlProps(data, key)} {...settingsProps(data)} />
      );

    case "grid_layout":
      return (
        <GridLayout {...htmlProps(data)} key={key} {...settingsProps(data)}>
          {data.children.map(cockpitComponent)}
        </GridLayout>
      );
    case "grid_cell":
      return (
        <GridCell {...htmlProps(data)} key={key} {...settingsProps(data)}>
          {data.children.map(cockpitComponent)}
        </GridCell>
      );
    case "grid_image_cell":
      return (
        <ImageCell {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "grid_category_cell":
      return (
        <CategoryCell {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "grid_product_cell":
      return (
        <ProductCell {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "grid_story_cell":
      return (
        <StoryCell {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );
    case "grid_news_cell":
      return (
        <NewsCell {...htmlProps(data)} key={key} {...settingsProps(data)} />
      );

    case "product_grid":
      return (
        <div {...htmlProps(data)} key={key}>
          <CockpitProductGrid
            ids={(data.settings.products || []).map(product => product._id)}
          />
        </div>
      );
    case "category_grid":
      return (
        <div {...htmlProps(data)} key={key}>
          <CockpitCategoryGrid items={data.settings.items || []} />
        </div>
      );
    case "story_grid":
      return (
        <div {...htmlProps(data)} key={key}>
          <CockpitStoryGrid
            limit={data.settings.limit || 6}
            categories={data.settings.categories}
            tag={data.settings.tag}
            types={data.settings.types}
            mode={data.settings.mode}
          />
        </div>
      );
    case "story_image":
      return <StoryImage key={key} {...settingsProps(data)} />;
    case "news_grid":
      return (
        <div {...htmlProps(data)} key={key}>
          <CockpitNewsGrid limit={data.settings.limit || 6} />
        </div>
      );
    case "guide_step":
      return <GuideStepCard key={key} {...settingsProps(data)} />;
    case "document":
      return (
        <div {...htmlProps(data)} key={key}>
          <a
            href={cmsUrl("/" + data.settings.file)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name="download" />
            {" " + data.settings.name}
          </a>
        </div>
      );
    case "claim_type_list":
      return (
        <div {...htmlProps(data)} key={key}>
          <CockpitClaimTypeList {...settingsProps(data)} />
        </div>
      );
    case "jotform":
      return (
        <ContentSection
          {...htmlProps(data)}
          key={key}
          theme={data.settings.theme}
        >
          {data.settings.heading && <h2>{data.settings.heading}</h2>}
          <JotForm formId={data.settings.jotform_id} />
        </ContentSection>
      );
    case "asset_list":
      return (
        <AssetList
          {...htmlProps(data)}
          key={key}
          heading={data.settings.heading}
          assets={data.settings.assets}
          type={data.settings.type}
        />
      );
    case "sub_menu":
      return <SubMenu {...htmlProps(data, key)} items={data.settings.items} />;
    case "link_list":
      return (
        <LinkList
          key={key}
          heading={data.settings.heading}
          items={data.settings.items}
          type={data.settings.type}
        />
      );
    case "usp_list":
      return (
        <USPList
          {...htmlProps(data)}
          key={key}
          heading={data.settings.heading}
          items={data.settings.items}
        />
      );

    case "table":
      return (
        <CockpitTable
          {...htmlProps(data)}
          key={key}
          content={data.settings.content}
          theme={data.settings.theme}
          zebra={data.settings.zebra}
          responsive={data.settings.responsive}
          hasHeader={data.settings.has_header}
          sortable={data.settings.sortable}
        />
      );

    case "products_menu":
      return (
        <ProductsMenu
          {...htmlProps(data)}
          key={key}
          config={data.settings.config}
        />
      );

    case "category_grid_menu":
      return (
        <CategoryGridMenu
          {...htmlProps(data)}
          key={key}
          items={data.settings.items || []}
        />
      );

    case "news_grid_menu":
      return <NewsGridMenu {...htmlProps(data)} key={key} />;

    case "story_grid_menu":
      return <StoryGridMenu {...htmlProps(data)} key={key} />;

    case "faq_list_menu":
      return <FaqListMenu {...htmlProps(data)} key={key} />;

    case "guide_list_menu":
      return <GuideListMenu {...htmlProps(data)} key={key} />;

    case "youtube_player":
      return (
        <CockpitYoutubePlayer
          {...htmlProps(data)}
          key={key}
          videoId={data.settings.video_id}
          title={data.settings.title}
        />
      );

    case "room_editor_button":
      return (
        <RoomEditorButton
          {...htmlProps(data)}
          key={key}
          initialFloor={data.settings.floor}
          title={data.settings.title}
        />
      );

    case "popup_fragment_button":
      return (
        <PopupFragmentButton
          {...htmlProps(data)}
          key={key}
          fragment={data.settings.fragment}
          title={data.settings.title}
          variant={data.settings.variant}
        />
      );

    case "guide_section": {
      const steps = [
        {
          component: "guide_section",
          settings: data.settings,
          children: data.children
        }
      ];
      return (
        <Guide {...htmlProps(data)} key={key} steps={steps} stepWidth={240} />
      );
    }
    case "_breadcrumb":
      return (
        <ContentSection key={key}>
          <Suspense fallback={<Spinner />}>
            <Breadcrumbs
              items={data.settings.paths.map(path => ({ path }))}
              childComponent={BreadcrumbPageItem}
            />
          </Suspense>
        </ContentSection>
      );

    default:
      addClass(data, "unknown-cockpit-component");
      console.warn("Unknown cockpit component", data);
      return <span {...htmlProps(data)} key={key} />;
  }
}

const CockpitLayoutComponentsWithoutNav = ({
  components,
  style,
  className
}) => {
  return Array.isArray(components) ? (
    <div
      className={classNames("cockpit-layout-components", className)}
      style={style}
    >
      {components.map(cockpitComponent)}
    </div>
  ) : null;
};

const CockpitLayoutComponents = ({
  components,
  withSectionNav = false,
  direction = "vertical",
  className = undefined,
  style = undefined
}) => {
  return Array.isArray(components) ? (
    withSectionNav ? (
      <SectionNavigation direction={direction} showMenu className={className}>
        {components}
      </SectionNavigation>
    ) : (
      <CockpitLayoutComponentsWithoutNav
        components={components}
        className={className}
        style={style || {}}
      />
    )
  ) : null;
};

export const CockpitComponent = ({ component }) => {
  //console.log("CockpitComponent:", component);
  return cockpitComponent(component);
};

export default CockpitLayoutComponents;
